import TrashIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant, useUserDisplayName } from "hooks/settings";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";
import { Link, useNavigate } from "react-router";
import {
  generatedApi,
  useDeletePeriodReservationMutation,
  useGetResolvedLabelReservationSettingsQuery,
  useGetReservedPeriodsQuery,
  useListInstallationLocationsWithBacklogQuery,
  useReserveLabellingPeriodsMutation,
  type LabelCoveragePriority,
  type ScrapEyeInstallationLocationWithBacklog,
} from "src/store/api/generatedApi";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useAppDispatch } from "src/store/store";
import { z } from "zod";
import { EVENT_LABELLING_DELETE_SCOPE } from "components/manualReview/hotkeyScopes";
import IdentifyEventsLabellerOptions from "./tool/IdentifyEventsLabellerOptions";
import { refreshReservedPeriods } from "./refreshReservedPeriods";

dayjs.extend(utc);
dayjs.extend(timezone);

export const IdentifyEventsPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const displayName = useUserDisplayName();
  const [hasAttemptedReservation, setHasAttemptedReservation] = useState(false);
  const {
    required_angles: defaultRequiredAngles,
    reservation: reservationConfig,
  } = useScrapEyeMetadata();

  const { data: resolvedSettings, isLoading: isLoadingResolvedSettings } =
    useGetResolvedLabelReservationSettingsQuery({ tenantName });

  const labellingDisabled = resolvedSettings?.maximum_number_of_labellers === 0;

  const [overrideSettings, setOverrideSettings] = useLocalStorageState(
    `identifyEvents.${tenantName}.overrideSettings`,
    false,
    { schema: z.boolean() }
  );

  const [reserveFromString, setReserveFromString] = useLocalStorageState<
    string | null
  >(`identifyEvents.${tenantName}.reserveFrom`, null, {
    schema: z.string().nullable(),
  });
  const reserveFrom: Dayjs | null = reserveFromString
    ? dayjs(reserveFromString)
    : null;
  const setReserveFrom = useCallback(
    (date: Dayjs | null) => {
      setReserveFromString(date?.isValid() ? date.toISOString() : null);
    },
    [setReserveFromString]
  );

  const [reserveUntilString, setReserveUntilString] = useLocalStorageState<
    string | null
  >(`identifyEvents.${tenantName}.reserveUntil`, null, {
    schema: z.string().nullable(),
  });
  const reserveUntil: Dayjs | null = reserveUntilString
    ? dayjs(reserveUntilString)
    : null;
  const setReserveUntil = useCallback(
    (date: Dayjs | null) => {
      setReserveUntilString(date?.isValid() ? date.toISOString() : null);
    },
    [setReserveUntilString]
  );

  const [labelCoveragePriority, setLabelCoveragePriority] =
    useLocalStorageState<LabelCoveragePriority>(
      `identifyEvents.${tenantName}.labelCoveragePriority`,
      reservationConfig.default_label_coverage_priority,
      {
        schema: z.enum([
          "breadth",
          "depth",
          "last_calendar_day_then_breadth",
          "last_calendar_day_then_depth",
          "recent_breadth",
          "recent_depth",
        ]),
      }
    );

  // null represents "auto"
  const [location, setLocation] = useState("auto");
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const dispatch = useAppDispatch();
  const { data: reservedPeriods, isFetching } = useGetReservedPeriodsQuery({
    location,
    tenantName,
  });

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- `?? []` creates a new array each render; depend on reservedPeriods instead
  const reservations = reservedPeriods?.reservations ?? [];
  const hasReservations = reservations.length > 0;
  const [requiredAngles, setRequiredAngles] = useLocalStorageState(
    `identifyEvents.${tenantName}.requiredAngles`,
    defaultRequiredAngles,
    { schema: z.array(z.string()) }
  );
  const [maxDurationSeconds, setMaxDurationSeconds] = useLocalStorageState(
    `identifyEvents.${tenantName}.maxDurationSeconds`,
    3600,
    { schema: z.number() }
  );
  const [allowedCoverageGapSeconds, setAllowedCoverageGapSeconds] =
    useLocalStorageState(
      `identifyEvents.${tenantName}.allowedCoverageGapSeconds`,
      60,
      { schema: z.number() }
    );
  const [ignoreCoverage, setIgnoreCoverage] = useLocalStorageState(
    `identifyEvents.${tenantName}.ignoreCoverage`,
    false,
    { schema: z.boolean() }
  );

  const hasNonDefaultValues = useMemo(() => {
    return (
      reserveFrom !== null ||
      reserveUntil !== null ||
      labelCoveragePriority !==
        reservationConfig.default_label_coverage_priority ||
      JSON.stringify(requiredAngles) !==
        JSON.stringify(defaultRequiredAngles) ||
      maxDurationSeconds !== 3600 ||
      allowedCoverageGapSeconds !== 60 ||
      ignoreCoverage
    );
  }, [
    reserveFrom,
    reserveUntil,
    labelCoveragePriority,
    reservationConfig.default_label_coverage_priority,
    requiredAngles,
    defaultRequiredAngles,
    maxDurationSeconds,
    allowedCoverageGapSeconds,
    ignoreCoverage,
  ]);

  const handleResetAll = useCallback(() => {
    setReserveFrom(null);
    setReserveUntil(null);
    setLabelCoveragePriority(reservationConfig.default_label_coverage_priority);
    setRequiredAngles(defaultRequiredAngles);
    setMaxDurationSeconds(3600);
    setAllowedCoverageGapSeconds(60);
    setIgnoreCoverage(false);
  }, [
    setReserveFrom,
    setReserveUntil,
    setLabelCoveragePriority,
    reservationConfig.default_label_coverage_priority,
    setRequiredAngles,
    defaultRequiredAngles,
    setMaxDurationSeconds,
    setAllowedCoverageGapSeconds,
    setIgnoreCoverage,
  ]);

  const [
    createReservation,
    { error: reservationError, isLoading: isCreatingReservation },
  ] = useReserveLabellingPeriodsMutation();
  const navigate = useNavigate();

  const { locationNames: locations } = useLabellingLocations();
  const isWaitingForResolvedSettings =
    !overrideSettings && isLoadingResolvedSettings;
  const { data: locationsDataRaw, isLoading: isLoadingLocations } =
    useListInstallationLocationsWithBacklogQuery(
      {
        tenantName,
        allowedCoverageGapSeconds: overrideSettings
          ? allowedCoverageGapSeconds
          : (resolvedSettings?.allowed_coverage_gap_seconds ?? undefined),
        requiredAngles: overrideSettings ? requiredAngles : undefined,
      },
      { skip: isWaitingForResolvedSettings }
    );
  const { enableScope } = useHotkeysContext();

  useEffect(() => {
    enableScope(EVENT_LABELLING_DELETE_SCOPE);
  }, [enableScope]);

  // Type guard and memoization for locations data
  const locationsData = useMemo<
    ScrapEyeInstallationLocationWithBacklog[]
  >(() => {
    if (!locationsDataRaw || !Array.isArray(locationsDataRaw)) return [];
    return locationsDataRaw;
  }, [locationsDataRaw]);

  const formatBacklogTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  const getLocationBacklog = (locationName: string): number => {
    const location = locationsData.find((l) => l.name === locationName);
    return location?.backlog_seconds ?? 0;
  };

  const getLocationBacklogBreakdown = (
    locationName: string
  ): Record<string, number> => {
    const location = locationsData.find((l) => l.name === locationName);
    return location?.backlog_by_label_count ?? {};
  };

  const formatLocationBacklogSummary = (locationName: string): string => {
    const breakdown = getLocationBacklogBreakdown(locationName);
    const entries = Object.entries(breakdown).sort(
      ([a], [b]) => parseInt(a) - parseInt(b)
    );

    if (entries.length === 0) return "";

    const parts = entries.map(([labelCount, seconds]) => {
      const count = parseInt(labelCount);
      const time = formatBacklogTime(seconds);
      if (count === 0) return `${time} unlabelled`;
      if (count === 1) return `${time} single pass`;
      if (count === 2) return `${time} double pass`;
      return `${time} ${count + 1}x pass`;
    });

    return ` (${parts.join(", ")})`;
  };

  useEffect(() => {
    const firstLocation = locations[0];
    if (firstLocation && !location) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- sets default location from async data
      setLocation(firstLocation);
    }
  }, [locations, location]);

  // The backend commits after it sends the response, so the refetch from a
  // mutation's own invalidation can read the old reservations. Force a second
  // refetch: tag invalidation is held until every pending request in the API
  // slice finishes, and the slow backlog query often keeps it waiting.
  const refreshReservedPeriodsAfterCommit = useCallback(() => {
    setTimeout(() => {
      void refreshReservedPeriods(dispatch, {
        location: locationRef.current,
        tenantName,
      });
    }, 200);
  }, [dispatch, tenantName]);

  const handleCreateReservation = useCallback(async () => {
    if (isCreatingReservation) return;
    setHasAttemptedReservation(true);
    const reservationResult = await createReservation(
      overrideSettings
        ? {
            tenantName,
            maxDurationSeconds,
            location: location,
            reserveFrom: reserveFrom?.isValid()
              ? reserveFrom.toISOString()
              : (reservationConfig.default_reserve_from ?? undefined),
            reserveUntil: reserveUntil?.isValid()
              ? reserveUntil.toISOString()
              : undefined,
            requiredAngles,
            allowedCoverageGapSeconds,
            ignoreCoverage,
            labelCoveragePriority,
          }
        : { tenantName }
    );
    if (reservationResult.data) {
      void dispatch(
        generatedApi.util.upsertQueryData(
          "getReservedPeriods",
          { location: locationRef.current, tenantName },
          reservationResult.data
        )
      );
    }
    refreshReservedPeriodsAfterCommit();
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- reservationConfig.default_reserve_from is only a fallback and should not re-trigger reservation creation
  }, [
    dispatch,
    refreshReservedPeriodsAfterCommit,
    createReservation,
    tenantName,
    overrideSettings,
    maxDurationSeconds,
    location,
    reserveFrom,
    reserveUntil,
    requiredAngles,
    allowedCoverageGapSeconds,
    ignoreCoverage,
    isCreatingReservation,
    labelCoveragePriority,
  ]);

  const [
    deletePeriodReservation,
    { isLoading: isRemovingReservations, isError: isRemovingReservationsError },
  ] = useDeletePeriodReservationMutation();

  const handleRemoveReservations = useCallback(async () => {
    const removedReservations: number[] = [];
    const promises = reservations.map((reservation) =>
      deletePeriodReservation({
        tenantName,
        labellingPeriodId: reservation.labelling_period_id,
      }).then(() => {
        removedReservations.push(reservation.labelling_period_id);
      })
    );
    await Promise.all(promises);
    refreshReservedPeriodsAfterCommit();
    setHasAttemptedReservation(false);
  }, [
    refreshReservedPeriodsAfterCommit,
    deletePeriodReservation,
    reservations,
    tenantName,
    setHasAttemptedReservation,
  ]);

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const firstStart = hasReservations
    ? Math.min(...reservations.map((r) => new Date(r.start).getTime()))
    : 0;
  const lastEnd = hasReservations
    ? Math.max(...reservations.map((r) => new Date(r.end).getTime()))
    : 0;
  const lastReservedUntil = hasReservations
    ? Math.max(...reservations.map((r) => new Date(r.reserved_until).getTime()))
    : 0;
  const durationMs = lastEnd - firstStart;

  const playerRoute = useMemo(() => {
    if (location !== "auto") {
      return `/${tenantName}/tool/identify-events/player/${location}`;
    }
    const firstReservation = reservations[0];
    if (firstReservation) {
      return `/${tenantName}/tool/identify-events/player/${firstReservation.location}`;
    }
    return undefined;
  }, [tenantName, location, reservations]);

  const { getHotkey, getDisplayKey } = useHotkeyContext();
  const submitEventsKey = getHotkey("submitEvents");
  useHotkeys(
    submitEventsKey,
    () => {
      if (hasReservations && playerRoute) {
        void navigate(playerRoute);
      } else {
        void handleCreateReservation();
      }
    },
    {
      keydown: true,
      enableOnFormTags: true,
    },
    [submitEventsKey, hasReservations, handleCreateReservation, playerRoute]
  );

  return (
    <AccountPageLayout title={t("identifyEvents")}>
      <Stack spacing={2} py={2}>
        <FormControl fullWidth>
          <InputLabel id="location-select-label">{t("location")}</InputLabel>
          <Select
            labelId="location-select-label"
            value={
              overrideSettings
                ? location
                : (resolvedSettings?.location ?? "auto")
            }
            label={t("location")}
            onChange={(e) => {
              setLocation(e.target.value);
            }}
            disabled={!overrideSettings}
          >
            <MenuItem value={"auto"}>{t("auto")}</MenuItem>
            {locations.map((loc) => {
              const backlogDisplay = formatLocationBacklogSummary(loc);
              return (
                <MenuItem key={loc} value={loc}>
                  {loc}
                  {backlogDisplay}
                </MenuItem>
              );
            })}
          </Select>
          {isLoadingLocations || isWaitingForResolvedSettings ? (
            <LinearProgress />
          ) : null}
        </FormControl>
        {location && getLocationBacklog(location) > 0 ? (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="h6">
                {t("availableFootageAtLocation", {
                  location,
                  duration: formatBacklogTime(getLocationBacklog(location)),
                })}
              </Typography>
              <Stack spacing={0.5}>
                {Object.entries(getLocationBacklogBreakdown(location))
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([labelCount, seconds]) => {
                    const count = parseInt(labelCount);
                    const priority =
                      count === 0
                        ? t("unlabeled")
                        : count === 1
                          ? t("needsSecondLabel")
                          : count === 2
                            ? t("needsThirdLabel")
                            : t("needsNthLabel", { n: count + 1 });
                    return (
                      <Typography
                        key={labelCount}
                        variant="body2"
                        color="text.secondary"
                      >
                        {priority}: {formatBacklogTime(seconds)}
                      </Typography>
                    );
                  })}
              </Stack>
            </Stack>
          </Paper>
        ) : null}
        <IdentifyEventsLabellerOptions
          overrideSettings={overrideSettings}
          setOverrideSettings={setOverrideSettings}
          reserveFrom={reserveFrom}
          setReserveFrom={setReserveFrom}
          reserveUntil={reserveUntil}
          setReserveUntil={setReserveUntil}
          labelCoveragePriority={labelCoveragePriority}
          setLabelCoveragePriority={setLabelCoveragePriority}
          onResetAll={handleResetAll}
          hasNonDefaultValues={hasNonDefaultValues}
          location={location === "auto" ? "" : location}
          requiredAngles={requiredAngles}
          setRequiredAngles={setRequiredAngles}
          maxDurationSeconds={maxDurationSeconds}
          setMaxDurationSeconds={setMaxDurationSeconds}
          allowedCoverageGapSeconds={allowedCoverageGapSeconds}
          setAllowedCoverageGapSeconds={setAllowedCoverageGapSeconds}
          ignoreCoverage={ignoreCoverage}
          setIgnoreCoverage={setIgnoreCoverage}
        />
        {reservationError ? (
          <Alert severity="error">
            {t("failedToCreateReservationPleaseTryAgain")}
          </Alert>
        ) : null}
        {isRemovingReservationsError ? (
          <Alert severity="error">
            {t("failedToRemoveReservationsPleaseTryAgain")}
          </Alert>
        ) : null}
        {(isCreatingReservation || (isFetching && hasAttemptedReservation)) &&
        !hasReservations ? (
          <Paper sx={{ p: 4 }}>
            <Stack spacing={3} alignItems="center">
              <SearchIcon sx={{ fontSize: 48, color: "primary.main" }} />
              <Typography variant="h6" align="center">
                {t("findingUnreservedFootageForYou")}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {t("thisMayTakeAFewSeconds")}
              </Typography>
              <Box sx={{ width: "100%", maxWidth: 400 }}>
                <LinearProgress />
              </Box>
            </Stack>
          </Paper>
        ) : null}
        {!isCreatingReservation &&
        !isFetching &&
        !reservationError &&
        reservations.length === 0 &&
        hasAttemptedReservation ? (
          <Alert severity="info">{t("noUnreservedFootageAvailable")}</Alert>
        ) : null}

        {hasReservations ? (
          <Paper sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Typography variant="h5">
                {t("hiUserName", { displayName })}
              </Typography>
              <Typography variant="body1">
                {t("assignedToReviewFootageMessage", {
                  firstStart: dayjs(firstStart).utc().format("L LT") + " UTC",
                  lastEnd: dayjs(lastEnd).utc().format("L LT") + " UTC",
                  lastReservedUntil:
                    dayjs(lastReservedUntil).format("L LT") +
                    ` (${dayjs(lastReservedUntil).format("z")})`,
                  location: reservations[0]?.location,
                })}
              </Typography>
              {playerRoute ? (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  size="large"
                  color="primary"
                  component={Link}
                  to={playerRoute}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("playDuration", { duration: formatDuration(durationMs) })}{" "}
                  [{getDisplayKey("submitEvents")}]
                </Button>
              ) : null}
            </Stack>
          </Paper>
        ) : null}
        {labellingDisabled && (
          <Alert severity="info">
            {t("labellingDisabledMaxLabellersZero")}
          </Alert>
        )}
        {!isCreatingReservation && !labellingDisabled && (
          <Stack direction="row" spacing={1}>
            {hasReservations ? (
              <Button
                variant="contained"
                startIcon={<TrashIcon />}
                onClick={() => void handleRemoveReservations()}
                loading={isRemovingReservations}
                color="secondary"
              >
                {t("removeAllMyReservations")}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => void handleCreateReservation()}
                color="primary"
              >
                {t("createNewReservations")} ({getDisplayKey("submitEvents")})
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </AccountPageLayout>
  );
};
